/* Hallmark · component: Toast · genre: modern-minimal · theme: maeiz-tokens
 * Silent success preferred for in-place results; use toast for failures & async outcomes.
 * Motion: opacity + transform only; honors reduced motion.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { clsx } from "clsx";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

export type ToastTone = "info" | "success" | "warning" | "danger";

export interface ToastItem {
    id: string;
    title?: string;
    message: string;
    tone?: ToastTone;
    durationMs?: number;
}

interface ToastContextValue {
    push: (toast: Omit<ToastItem, "id">) => void;
    dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error("useToast must be used within ToastProvider");
    return ctx;
}

const toneBox: Record<ToastTone, string> = {
    info: "border-primary/20 bg-white dark:border-gold/25 dark:bg-neutral-navy",
    success: "border-success/30 bg-white dark:bg-neutral-navy",
    warning: "border-warning/35 bg-white dark:bg-neutral-navy",
    danger: "border-danger/35 bg-white dark:bg-neutral-navy",
};

const toneIconWrap: Record<ToastTone, string> = {
    info: "bg-primary/10 text-primary dark:bg-gold/15 dark:text-gold-light",
    success: "bg-success/10 text-success",
    warning: "bg-warning/10 text-warning",
    danger: "bg-danger/10 text-danger",
};

const toneIcon: Record<ToastTone, React.ReactNode> = {
    info: <Info size={16} strokeWidth={2} aria-hidden />,
    success: <CheckCircle2 size={16} strokeWidth={2} aria-hidden />,
    warning: <AlertTriangle size={16} strokeWidth={2} aria-hidden />,
    danger: <AlertCircle size={16} strokeWidth={2} aria-hidden />,
};

function ToastCard({
    item,
    onDismiss,
}: {
    item: ToastItem;
    onDismiss: (id: string) => void;
}) {
    const [entered, setEntered] = useState(false);
    const tone = item.tone ?? "info";

    useEffect(() => {
        const id = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(id);
    }, []);

    return (
        <div
            role="status"
            className={clsx(
                "pointer-events-auto flex gap-3 rounded-xl border px-3.5 py-3 shadow-mz-lg",
                "transition-[opacity,transform] duration-200 ease-out",
                "motion-reduce:transition-none motion-reduce:transform-none",
                entered ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
                toneBox[tone],
            )}
        >
            <div
                className={clsx(
                    "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                    toneIconWrap[tone],
                )}
            >
                {toneIcon[tone]}
            </div>
            <div className="min-w-0 flex-1 text-sm">
                {item.title ? (
                    <p className="font-semibold text-neutral-ink dark:text-white">{item.title}</p>
                ) : null}
                <p className={clsx("text-neutral-secondary dark:text-slate-300", item.title && "mt-0.5")}>
                    {item.message}
                </p>
            </div>
            <button
                type="button"
                className="shrink-0 self-start rounded-lg p-1 text-neutral-secondary hover:bg-black/5 hover:text-neutral-ink dark:hover:bg-white/10 dark:hover:text-white"
                aria-label="Dismiss notification"
                onClick={() => onDismiss(item.id)}
            >
                <X size={16} strokeWidth={2} />
            </button>
        </div>
    );
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<ToastItem[]>([]);

    const dismiss = useCallback((id: string) => {
        setItems((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const push = useCallback(
        (toast: Omit<ToastItem, "id">) => {
            const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
            setItems((prev) => [...prev, { ...toast, id }]);
            const ms = toast.durationMs ?? (toast.tone === "danger" ? 6500 : 4200);
            window.setTimeout(() => dismiss(id), ms);
        },
        [dismiss],
    );

    const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div
                className="pointer-events-none fixed bottom-4 end-4 z-toast flex w-[min(100%-2rem,380px)] flex-col gap-2"
                aria-live="polite"
            >
                {items.map((t) => (
                    <ToastCard key={t.id} item={t} onDismiss={dismiss} />
                ))}
            </div>
        </ToastContext.Provider>
    );
};
